import { DeviceBillingState, DeviceEntryProps, DeviceType, TCoords, firmwareTypeForDevice } from '@ixicare/resources';
import { NavigateFunction } from 'react-router-dom';
import {
  Ban,
  HardDriveUpload,
  DatabaseZap,
  FolderInput,
  Map,
  PackageOpen,
  Settings,
  SquarePen,
  Trash2,
} from 'lucide-react';
import { TableRowAction } from './hooks/useTableRowActions';
import { t } from '~i18n';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';

export type ConfigurableDevice = DeviceEntryProps & { type: DeviceType };

const isConfigurable = (dbg: DBGWithType): dbg is ConfigurableDevice => 'uuid' in dbg && typeof dbg.uuid === 'string';

type MapLocation = {
  planeId?: number;
  installationCoords?: TCoords;
  id: number | string;
};

export const navigateMapAction = <T extends MapLocation>(
  navigate: NavigateFunction,
  siteUuid: string | undefined,
  isArchived: boolean,
  markerType: string | ((item: T) => string)
): TableRowAction<T> => ({
  icon: 'residence-map',
  lucide: Map,
  label: t('devices.table.map'),
  // TODO(bulk): acts on the first selected row until bulk behavior is wired.
  onClick: (items) => {
    const item = items[0];
    if (!item) return;
    const type = typeof markerType === 'function' ? markerType(item) : markerType;
    navigate(`/sites/${siteUuid}/${item.planeId}?marker=${type.toLowerCase()}::${item.id}`);
  },
  isVisible: (item) => !!item.installationCoords && !isArchived,
});

export const configAction = (onClick: (items: ConfigurableDevice[]) => void): TableRowAction<DBGWithType> => ({
  icon: 'settings',
  lucide: Settings,
  label: t('devices.table.configuration'),
  onClick: (items) => {
    const configurable = items.filter(isConfigurable);
    if (configurable.length) onClick(configurable);
  },
  isVisible: isConfigurable,
});

export const editAction = <T>(onEdit: (items: T[]) => void): TableRowAction<T> => ({
  icon: 'edit',
  lucide: SquarePen,
  label: t('devices.table.edit'),
  onClick: onEdit,
});

export const moveAction = <T>(onMove: (items: T[]) => void): TableRowAction<T> => ({
  icon: 'move',
  lucide: FolderInput,
  label: t('devices.table.move'),
  onClick: onMove,
});

export const deleteAction = <T>(onDelete: (items: T[]) => void): TableRowAction<T> => ({
  icon: 'delete',
  lucide: Trash2,
  label: t('devices.table.archive'),
  onClick: (items) => {
    if (items.length) onDelete(items);
  },
});

/**
 * Retire hardware for good. Separate from archiving, which keeps billing and lets the
 * same device be added again — decommissioning is what takes it off the invoice and
 * blocks the re-add, so it is only offered on already-archived rows and never for
 * hardware that is decommissioned already.
 */
export const decommissionAction = <T extends { billingState?: DeviceBillingState }>(
  onClick: (items: T[]) => void
): TableRowAction<T> => ({
  icon: 'decommission',
  lucide: Ban,
  label: t('device.decommission.action'),
  onClick: (items) => {
    if (items.length) onClick(items);
  },
  isVisible: (item) => item.billingState !== DeviceBillingState.DECOMMISSIONED,
});

/**
 * Take hardware out of service: it stops counting, and if it was permanently deleted the
 * block on adding it again is lifted. Unarchiving puts it back into service.
 *
 * Offered on archived and permanently deleted rows. Never on a device that is live at a
 * site — an unarchived device always counts, and the backend refuses that with a 409.
 *
 * Ixi employees only — the employee check is the caller's.
 */
export const resetAction = <T extends { billingState?: DeviceBillingState }>(
  onClick: (items: T[]) => void
): TableRowAction<T> => ({
  icon: 'reset',
  lucide: PackageOpen,
  label: t('device.reset.action'),
  onClick: (items) => {
    if (items.length) onClick(items);
  },
  isVisible: (item) =>
    item.billingState === DeviceBillingState.DECOMMISSIONED || item.billingState === DeviceBillingState.ARCHIVED,
});

export const deleteDeviceDataAction = <T>(onClick: (items: T[]) => void): TableRowAction<T> => ({
  icon: 'delete-data',
  lucide: DatabaseZap,
  label: t('devices.table.deleteData'),
  onClick: (items) => {
    if (items.length) onClick(items);
  },
});

const isUpdatableDevice = (dbg: DBGWithType): dbg is DBGWithType & DeviceEntryProps =>
  'deviceType' in dbg && !!firmwareTypeForDevice(dbg.deviceType);

/** Plan a software or firmware update. Only for device types known to take OTA updates. */
export const updateFirmwareAction = (onClick: (items: DeviceEntryProps[]) => void): TableRowAction<DBGWithType> => ({
  icon: 'update-firmware',
  lucide: HardDriveUpload,
  label: t('firmware.update.actionSoftware'),
  onClick: (items) => {
    const updatable = items.filter(isUpdatableDevice);
    if (updatable.length) onClick(updatable);
  },
  isVisible: isUpdatableDevice,
});
